import { useDispatch, useSelector, useStore } from "react-redux";
import { EventStatus } from "@prestosports/prestostats-core/Common";
import { getSafe } from "../../util/object-util";
import {
	clearEvents as clearEventsAction,
	getCompletedEvents as getCompletedEventsAction,
	getEvents as getEventsAction,
	getEventsSummary as getEventsSummaryAction,
	getGameConfiguration as getGameConfigurationAction,
	getInProgressEvents as getInProgressEventsAction,
	getUpcomingEvents as getUpcomingEventsAction,
	overWriteEventData as overWriteEventDataAction,
	resetEventData as resetEventDataAction,
	restoreDefaultGameConfiguration as restoreDefaultGameConfigurationAction,
	saveEvent as saveEventAction,
	saveGameConfiguration as saveGameConfigurationAction,
	saveFirstDownYardSet as saveFirstDownYardSetAction,
	selectEventStream as selectEventStreamAction,
	setGameInProgress as setGameInProgressAction,
	setRuleSetId as setRuleSetIdAction
} from "./actions";
import { useAppContext } from "../app/context";
import AppConfig from "../../config/app-config";

const useEventContext = () => {
	const dispatch = useDispatch();
	const store = useStore();
	const { redirect } = useAppContext();
	const defaultPagingOptions = {
		pageNumber: 0,
		pageSize: AppConfig.EVENTS.PAGINATION.PAGE_SIZE
	};

	const useEvents = () => useSelector((state) => state.events.events);

	const useIsLoading = () => useSelector((state) => state.events.isLoading);

	const useInProgressEvents = () =>
		useSelector((state) => state.events.inProgressEvents);

	const useCompletedEvents = () =>
		useSelector((state) => state.events.completedEvents);

	const useEventsSummary = () =>
		useSelector((state) => state.events.eventsSummary);

	const useCurrentEvent = () =>
		useSelector((state) => state.events.currentEvent);

	const useUpcomingEvents = () =>
		useSelector((state) => state.events.upcomingEvents);

	const useCurrentEventSettings = () =>
		useSelector((state) => state.events.currentEventSettings);

	const useTimeoutsPerHalf = () =>
		useSelector((state) =>
			getSafe(
				() =>
					state.events.currentEventSettings.gameConfiguration.general
						.timeoutsPerHalf,
				3
			)
		);

	const useMaxMinutesByPeriod = () =>
		useSelector((state) =>
			getSafe(
				() =>
					state.events.currentEventSettings.gameConfiguration.general
						.minutesByPeriod,
				15
			)
		);

	const useValidationErrors = () =>
		useSelector((state) => state.events.validationErrors);

	const useRemoteEventChanged = () =>
		useSelector((state) => state.events.remoteEventChanged);

	const useCanEditCurrentEvent = () =>
		useSelector(({ events }) => {
			const { currentEvent } = events;
			return currentEvent && !EventStatus.isFinal(currentEvent);
		});

	const state = {
		events: useEvents,
		upcomingEvents: useUpcomingEvents,
		completedEvents: useCompletedEvents,
		inProgressEvents: useInProgressEvents,
		isLoading: useIsLoading,
		eventsSummary: useEventsSummary,
		currentEvent: useCurrentEvent,
		currentEventSettings: useCurrentEventSettings,
		timeoutsPerHalf: useTimeoutsPerHalf,
		maxMinutesByPeriod: useMaxMinutesByPeriod,
		validationErrors: useValidationErrors,
		remoteEventChanged: useRemoteEventChanged,
		canEditCurrentEvent: useCanEditCurrentEvent
	};

	const getEvents = () => {
		getEventsAction(dispatch);
	};

	const clearEvents = () => {
		clearEventsAction(dispatch);
	};

	const getEventsSummary = () => {
		const { selectedSport, selectedDate } = store.getState().app;
		if (selectedSport?.length) {
			getEventsSummaryAction(selectedDate, selectedSport, dispatch);
		}
	};

	const getGameConfiguration = () => {
		const { currentEvent } = store.getState().events;
		getGameConfigurationAction(currentEvent ? currentEvent.id : null, dispatch);
	};

	const saveGameConfiguration = (gameConfiguration) => {
		const { currentEvent } = store.getState().events;
		if (currentEvent) {
			saveGameConfigurationAction(currentEvent.id, gameConfiguration, dispatch);
		}
	};

	const saveFirstDownYardSet = (spots) => {
		const { currentEvent } = store.getState().events;
		if (currentEvent) {
			return saveFirstDownYardSetAction(currentEvent.id, spots, dispatch);
		}
		return Promise.resolve();
	};

	const setRuleSetId = (ruleSetId) => {
		const { currentEvent } = store.getState().events;
		if (currentEvent) {
			setRuleSetIdAction(currentEvent, ruleSetId, dispatch);
		}
	};

	const restoreDefaultGameConfiguration = () => {
		const { currentEvent } = store.getState().events;
		if (currentEvent) {
			restoreDefaultGameConfigurationAction(currentEvent.id, dispatch);
		}
	};

	const saveEvent = (event) => {
		saveEventAction(event, dispatch);
	};

	const overWriteEventData = () => {
		const { currentEvent } = store.getState().events;
		overWriteEventDataAction(currentEvent.id, dispatch);
	};

	const resetEventData = () => {
		const { currentEvent } = store.getState().events;
		resetEventDataAction(currentEvent.id, redirect, dispatch);
	};

	const getUpcomingEvents = (seasonId, filterOptions = {}, pagingOptions) => {
		if (seasonId) {
			getUpcomingEventsAction(
				seasonId,
				{},
				filterOptions,
				Object.assign(defaultPagingOptions, pagingOptions),
				dispatch
			);
		}
	};

	const getInProgressEvents = (seasonId, filterOptions = {}, pagingOptions) => {
		if (seasonId) {
			getInProgressEventsAction(
				seasonId,
				{},
				filterOptions,
				Object.assign(defaultPagingOptions, pagingOptions),
				dispatch
			);
		}
	};

	const getCompletedEvents = (seasonId, filterOptions = {}, pagingOptions) => {
		if (seasonId) {
			getCompletedEventsAction(
				seasonId,
				{},
				filterOptions,
				Object.assign(defaultPagingOptions, pagingOptions),
				dispatch
			);
		}
	};

	const selectEventStream = (eventId, streamId) => {
		selectEventStreamAction(eventId, streamId, dispatch);
	};

	const setGameInProgress = (eventId) =>
		setGameInProgressAction(eventId, dispatch);

	return {
		state,
		getEvents,
		getEventsSummary,
		clearEvents,
		getGameConfiguration,
		saveGameConfiguration,
		saveFirstDownYardSet,
		restoreDefaultGameConfiguration,
		saveEvent,
		overWriteEventData,
		resetEventData,
		getUpcomingEvents,
		getInProgressEvents,
		getCompletedEvents,
		setRuleSetId,
		selectEventStream,
		setGameInProgress
	};
};

export { useEventContext };
